import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { SearchComponent } from 'src/app/layouts/search/search.component';
import { GetCandidateResumeJobsHomePage, GetJobsHomePage, GetJobsHomePageSummary } from 'src/app/models/homePage/jobs-home-page';
import { DropDownKeywords, DropdownOption } from 'src/app/models/shared/dropdown-option';
import { Resume } from 'src/app/models/resume/resume';
import { DropdownService } from 'src/app/services/dropdown.service';
import { FileStorageService } from 'src/app/services/file-storage.service';
import { JobApplicationService } from 'src/app/services/job-application.service';
import { JobsHomePageService } from 'src/app/services/jobs-home-page.service';
import { ResumeService } from 'src/app/services/resume.service';
import Swal from 'sweetalert2';

type JobOfferOption = DropdownOption & {
  selected?: boolean;
}

type JobsHomePage = GetJobsHomePage & {
  skillList?: string[];
  softSkillsList?: string[];
  languagesList?: {
    name: string;
    level: string;
  }[];
  publicOnDescripcion?: string;
  logoUrl?: string;
}

type CandidateResume = GetCandidateResumeJobsHomePage & {
  skillList: string[];
  softSkillsList: string[];
  languagesList: {
    name: string;
    level: string;
  }[];
  photoUrl: string;
}

@Component({
  selector: 'app-main-page',
  templateUrl: './main.component.html',
  styles: [],
  standalone: true,
  imports: [CommonModule, SearchComponent, FormsModule, RouterLink]
})
export class MainPageComponent implements OnInit {
  private readonly JobsHomePageService = inject(JobsHomePageService);
  private readonly dropdownService = inject(DropdownService);
  private readonly docService = inject(FileStorageService);
  private readonly jobApplicationService = inject(JobApplicationService);
  private readonly resumeService = inject(ResumeService);
  private readonly cdr = inject(ChangeDetectorRef);

  // Tab 1: Ofertas (typeId = 1)
  pageOffers: number = 1;
  pageSizeOffers: number = 1;
  totalItemsOffers: number = 0;
  totalPagesOffers: number = 0;

  // Tab 2: Favoritos (typeId = 2)
  pageFavorites: number = 1;
  pageSizeFavorites: number = 1;
  totalItemsFavorites: number = 0;
  totalPagesFavorites: number = 0;

  // Tab 3: No me interesa (typeId = 3)
  pageNotInterested: number = 1;
  pageSizeNotInterested: number = 1;
  totalItemsNotInterested: number = 0;
  totalPagesNotInterested: number = 0;

  resume = signal<CandidateResume>({
    id: null,
    name: '',
    description: '',
    userProfilePictureId: null,
    userName: '',
    skills: '',
    languages: '',
    softSkills: '',
    skillList: [],
    softSkillsList: [],
    languagesList: [],
    photoUrl: ''
  });

  resumes = signal<Resume[]>([]);
  loadingResumes = signal<boolean>(false);
  showResumeSelector = signal<boolean>(false);
  selectedResumeId = signal<number | null>(null);

  typeId = signal<number>(1);

  jobBenefits: JobOfferOption[] = [];
  showAllBenefits = signal<boolean>(false);

  salaryRange = signal<boolean>(false);
  salaryRangeFrom = signal<number>(0);
  salaryRangeTo = signal<number>(0);
  provinceId = signal<number>(0);
  departmentId = signal<number>(0);
  positionId = signal<number>(0);

  // Variables para cada tab
  jobsHomePageOffers = signal<JobsHomePage | null>(null);
  jobsHomePageFavorites = signal<JobsHomePage | null>(null);
  jobsHomePageNotInterested = signal<JobsHomePage | null>(null);

  // Variable activa según el tab seleccionado
  activeJobsHomePage(){
    const currentTabType = this.typeId();
    if (currentTabType === 1) return this.jobsHomePageOffers();
    if (currentTabType === 2) return this.jobsHomePageFavorites();
    if (currentTabType === 3) return this.jobsHomePageNotInterested();
    return this.jobsHomePageOffers();
  }

  matchPercent = computed(() => {
    return this.activeJobsHomePage()?.matchPercent ?? 0;
  });
  
  // Métodos para obtener la página actual y total de páginas según el tab activo
  getCurrentPage(): number {
    const currentTabType = this.typeId();
    if (currentTabType === 1) return this.pageOffers;
    if (currentTabType === 2) return this.pageFavorites;
    if (currentTabType === 3) return this.pageNotInterested;
    return this.pageOffers;
  }
  
  getTotalPages(): number {
    const currentTabType = this.typeId();
    if (currentTabType === 1) return this.totalPagesOffers;
    if (currentTabType === 2) return this.totalPagesFavorites;
    if (currentTabType === 3) return this.totalPagesNotInterested;
    return this.totalPagesOffers;
  }

  summary: GetJobsHomePageSummary = {
    totalMatches: 0,
    recentThisWeek: 0,
    remoteOffers: 0,
    withHighAffinity: 0
  }

  jobOfferStats: DropdownOption[] = [];

  loadingResume = signal<boolean>(false);
  loadingJobs = signal<boolean>(false);

  constructor() {

  }

  getOptions() {
    this.dropdownService.GetOptions(DropDownKeywords.JobBenefits).subscribe({
      next: res => {
        this.jobBenefits = res;
      }
    });
    
    // Obtener estadísticas de ofertas de trabajo
    this.dropdownService.GetOptions(DropDownKeywords.JobOfferStats).subscribe({
      next: res => {
        this.jobOfferStats = res;
        // Actualizar el resumen con las estadísticas de la base de datos
        const thisWeekStats = res.find(x => x.id === 2);
        const remoteStats = res.find(x => x.id === 4);
        
        if (thisWeekStats) {
          this.summary.recentThisWeek = thisWeekStats.relatedId || 0;
        }
        
        if (remoteStats) {
          this.summary.remoteOffers = remoteStats.relatedId || 0;
        }
      }
    });
  }

  ngOnInit(): void {
    this.getOptions();
    this.getResumeJobsHomePage();
  }

  loadResumes(): void {
    this.loadingResumes.set(true);
    this.showResumeSelector.set(true);
    
    this.resumeService.getResumes().subscribe({
      next: (resumes) => {
        this.resumes.set(resumes);
        this.loadingResumes.set(false);
      },
      error: (error) => {
        console.error('Error al cargar currículums:', error);
        this.loadingResumes.set(false);
      }
    });
  }

  selectResume(resumeId: number): void {
    if (!resumeId) return;
    
    this.selectedResumeId.set(resumeId);
    this.loadingResume.set(true);
    
    this.JobsHomePageService.getCandidateResumeJobsHomePage(resumeId).subscribe({
      next: res => {
        this.loadingResume.set(false);
        if (!res) {
          return;
        }
        
        this.showResumeSelector.set(false);
        this.resume.set({
          ...res,
          skillList: res.skills ? res.skills?.split(',').map(x => x.trim()) : [],
          softSkillsList: res.softSkills ? res.softSkills?.split(',').map(x => x.trim()) : [],
          languagesList: res.languages ? res.languages?.split(',').map(x => {
            const [name, level] = x.split(':');
            return {
              name: name?.trim(),
              level: level?.trim()
            };
          }) : [],
          photoUrl: res?.userProfilePictureId ? this.docService.getFileUrl(`${res.userProfilePictureId}`) : ''
        });
        
        if (this.resume().id) {
          this.getJobsHomePageSummary();
          this.getJobsHomePage();
        }
      },
      error: err => {
        this.loadingResume.set(false);
        console.error('Error al cargar el currículum seleccionado:', err);
      }
    });
  }

  getResumeJobsHomePage() {
    this.loadingResume.set(true);
    this.JobsHomePageService.getCandidateResumeJobsHomePage().subscribe({
      next: res => {
        this.loadingResume.set(false);
        if (!res || !res.id) {
          // Si no hay contenido, cargar la lista de resumes
          this.loadResumes();
          return;
        }
        this.showResumeSelector.set(false);
        this.resume.set({
          ...res,
          skillList: res.skills ? res.skills?.split(',').map(x => x.trim()) : [],
          softSkillsList: res.softSkills ? res.softSkills?.split(',').map(x => x.trim()) : [],
          languagesList: res.languages ? res.languages?.split(',').map(x => {
            const [name, level] = x.split(':');
            return {
              name: name?.trim(),
              level: level?.trim()
            };
          }) : [],
          photoUrl: res?.userProfilePictureId ? this.docService.getFileUrl(`${res.userProfilePictureId}`) : ''
        });
        if (this.resume().id) {
          this.getJobsHomePageSummary();
          this.getJobsHomePage();
        }
      },
      error: err => {
        this.loadingResume.set(false);
        // En caso de error, también intentamos cargar la lista de resumes
        this.loadResumes();
      }
    });
  }

  getJobsHomePage() {
    this.loadingJobs.set(true);
    const resumeId = this.resume().id;
    if (!resumeId) {
      return;
    }

    // Determinar qué variables de paginación usar según el tab activo
    let currentPage: number;
    let currentPageSize: number;
    
    // Guardar el tipo de tab actual para usar en la respuesta
    const currentTabType = this.typeId();

    switch(currentTabType) {
      case 1: // Ofertas
        currentPage = this.pageOffers;
        currentPageSize = this.pageSizeOffers;
        console.log(`Solicitando ofertas - Página ${currentPage} de ${this.totalPagesOffers}`);
        break;
      case 2: // Favoritos
        currentPage = this.pageFavorites;
        currentPageSize = this.pageSizeFavorites;
        console.log(`Solicitando favoritos - Página ${currentPage} de ${this.totalPagesFavorites}`);
        break;
      case 3: // No me interesa
        currentPage = this.pageNotInterested;
        currentPageSize = this.pageSizeNotInterested;
        console.log(`Solicitando no interesa - Página ${currentPage} de ${this.totalPagesNotInterested}`);
        break;
      default:
        currentPage = this.pageOffers;
        currentPageSize = this.pageSizeOffers;
    }

    this.JobsHomePageService.getJobsHomePage({
      page: currentPage,
      pageSize: currentPageSize,
      typeId: this.typeId(),
      resumeId: resumeId,
      salaryRange: this.salaryRange(),
      salaryRangeFrom: this.salaryRangeFrom(),
      salaryRangeTo: this.salaryRangeTo(),
      provinceId: this.provinceId(),
      departmentId: this.departmentId(),
      positionId: this.positionId(),
      insurenceJson: JSON.stringify(this.jobBenefits.filter(x => x.selected).map(x => ({
        id: x.id,
        description: x.text
      })))
    }).subscribe({
      next: res => {
        this.loadingJobs.set(false);
        console.log('Respuesta del backend:', { 
          page: res.page, 
          totalPages: res.totalPages, 
          totalItems: res.totalItems,
          currentTabType
        });
        const item = res.items?.at(0);

        // Procesar el resultado
        const processedItem = item ? {
          ...item,
          skillList: item.skills ? item.skills?.split(',').map(x => x.trim()) : [],
          softSkillsList: item.softSkills ? item.softSkills?.split(',').map(x => x.trim()) : [],
          languagesList: item.languages ? item.languages?.split(',').map(x => {
            const [name, level] = x.split(':');
            return {
              name: name?.trim(),
              level: level?.trim()
            };
          }) : [],
          publicOnDescripcion: item.publicOn ? this.getRelativeTime(item.publicOn) : '',
          logoUrl: item?.logoFileId ? this.docService.getFileUrl(`${item.logoFileId}`) : ''
        } : null;

        // Actualizar la variable correspondiente según el tab activo que estaba activo cuando se hizo la solicitud
        switch(currentTabType) {
          case 1: // Ofertas
            this.jobsHomePageOffers.set(processedItem);
            this.totalItemsOffers = res.totalItems;
            this.totalPagesOffers = res.totalPages;
            // No sobrescribir la página actual con la del backend
            // this.pageOffers = res.page;
            this.pageSizeOffers = res.pageSize;
            break;
          case 2: // Favoritos
            this.jobsHomePageFavorites.set(processedItem);
            this.totalItemsFavorites = res.totalItems;
            this.totalPagesFavorites = res.totalPages;
            // No sobrescribir la página actual con la del backend
            // this.pageFavorites = res.page;
            this.pageSizeFavorites = res.pageSize;
            break;
          case 3: // No me interesa
            this.jobsHomePageNotInterested.set(processedItem);
            this.totalItemsNotInterested = res.totalItems;
            this.totalPagesNotInterested = res.totalPages;
            // No sobrescribir la página actual con la del backend
            // this.pageNotInterested = res.page;
            this.pageSizeNotInterested = res.pageSize;
            break;
        }
        
        // Forzar la detección de cambios para actualizar la UI
        this.cdr.detectChanges();
      },
      error: err => {
        this.loadingJobs.set(false);
      }
    });
  }

  getJobsHomePageSummary(){
    const resumeId = this.resume().id;
    if (!resumeId) {
      return;
    }
    this.JobsHomePageService.getJobsHomePageSummary(resumeId).subscribe({
      next: res => {
        this.summary = res;
      },
      error: err => {
      }
    });
  }

  getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diff = (now.getTime() - date.getTime()) / 1000;

    const intervals: { [key: string]: number } = {
      año: 31536000,
      mes: 2592000,
      día: 86400,
      hora: 3600,
      minuto: 60,
      segundo: 1
    };

    for (const key in intervals) {
      const value = intervals[key];
      const count = Math.floor(diff / value);
      if (count >= 1) {
        return `Hace ${count} ${key}${count > 1 ? 's' : ''}`;
      }
    }
    return 'Justo ahora';
  }

  changeSearch(filter:
    {
      provinceId: number,
      departmentId: number,
      positionId: number
    }) {
    this.provinceId.set(filter.provinceId);
    this.departmentId.set(filter.departmentId);
    this.positionId.set(filter.positionId);
    this.getJobsHomePage();
  }

  nextPage() {
    // Determinar qué variables de paginación usar según el tab activo
    switch(this.typeId()) {
      case 1: // Ofertas
        if (this.pageOffers < this.totalPagesOffers) {
          this.pageOffers++;
          console.log('Avanzando a página ' + this.pageOffers + ' de ' + this.totalPagesOffers + ' en tab Ofertas');
          this.getJobsHomePage();
          this.cdr.detectChanges();
        }
        break;
      case 2: // Favoritos
        if (this.pageFavorites < this.totalPagesFavorites) {
          this.pageFavorites++;
          console.log('Avanzando a página ' + this.pageFavorites + ' de ' + this.totalPagesFavorites + ' en tab Favoritos');
          this.getJobsHomePage();
          this.cdr.detectChanges();
        }
        break;
      case 3: // No me interesa
        if (this.pageNotInterested < this.totalPagesNotInterested) {
          this.pageNotInterested++;
          console.log('Avanzando a página ' + this.pageNotInterested + ' de ' + this.totalPagesNotInterested + ' en tab No me interesa');
          this.getJobsHomePage();
          this.cdr.detectChanges();
        }
        break;
    }
  }

  previousPage() {
    // Determinar qué variables de paginación usar según el tab activo
    switch(this.typeId()) {
      case 1: // Ofertas
        if (this.pageOffers > 1) {
          this.pageOffers--;
          console.log('Retrocediendo a página ' + this.pageOffers + ' de ' + this.totalPagesOffers + ' en tab Ofertas');
          this.getJobsHomePage();
          this.cdr.detectChanges();
        }
        break;
      case 2: // Favoritos
        if (this.pageFavorites > 1) {
          this.pageFavorites--;
          console.log('Retrocediendo a página ' + this.pageFavorites + ' de ' + this.totalPagesFavorites + ' en tab Favoritos');
          this.getJobsHomePage();
          this.cdr.detectChanges();
        }
        break;
      case 3: // No me interesa
        if (this.pageNotInterested > 1) {
          this.pageNotInterested--;
          console.log('Retrocediendo a página ' + this.pageNotInterested + ' de ' + this.totalPagesNotInterested + ' en tab No me interesa');
          this.getJobsHomePage();
          this.cdr.detectChanges();
        }
        break;
    }
  }

  changeTab(typeId: number) {
    // Guardar el tipo de tab anterior para depuración
    const previousTabType = this.typeId();
    
    // Cambiar al nuevo tab
    this.typeId.set(typeId);
    
    console.log(`Cambiando de tab ${previousTabType} a tab ${typeId}`);
    
    // Al cambiar de tab, siempre usamos los datos de paginación específicos de ese tab
    // que ya están configurados, no necesitamos reiniciarlos
    this.getJobsHomePage();
    this.cdr.detectChanges();
  }

  addJobApplicationsNotInterested() {
    const jobOfferId = this.activeJobsHomePage()?.id;
    if (!jobOfferId) {
      return;
    }
    this.JobsHomePageService.addJobApplicationsNotInterested(jobOfferId).subscribe({
      next: res => {
        this.getJobsHomePage();
        this.cdr.detectChanges();
      },
      error: err => {
        console.error('Error al marcar como no interesado:', err);
        this.cdr.detectChanges();
      }
    });
  }

  addJobApplicationsFavorite() {
    const jobOfferId = this.activeJobsHomePage()?.id;
    if (!jobOfferId) {
      return;
    }
    this.JobsHomePageService.addJobApplicationsFavorite(jobOfferId).subscribe({
      next: res => {
        this.getJobsHomePage();
        this.cdr.detectChanges();
      },
      error: err => {
        console.error('Error al marcar como favorito:', err);
        this.cdr.detectChanges();
      }
    });
  }

  applyOffer() {
    const jobOfferId = this.activeJobsHomePage()?.id;
    if (!jobOfferId) {
      return;
    }

    const resume = this.resume();

    Swal.fire({
      title: 'Aplicar a la oferta laboral',
      text: '¿Está seguro de que desea aplicar a esta oferta laboral?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, aplicar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        if (!resume?.id) {
          return;
        }
        this.jobApplicationService.addJobApplication({
          jobApplicationId: null,
          jobId: jobOfferId,
          resumeId: resume.id,
          statusId: 0,
          requestedByEmployeeId: null,
          recommendedByOperatorId: null,
          isEnabled: false
        }).subscribe({
          next: res => {
            Swal.fire({
              title: '',
              text: 'Se aplicó a la oferta laboral',
              icon: 'success',
              confirmButtonColor: '#d33'
            });
            this.getJobsHomePage();
            this.cdr.detectChanges();
          }, error: res => {
            Swal.fire({
              title: 'Error',
              text: 'Ocurrió un error al aplicar a la oferta laboral.',
              icon: 'error',
              confirmButtonColor: '#d33'
            });
            this.cdr.detectChanges();
          }
        });
      }
    });

  }

}